import { Create, Divider, Form, Input, SaveButton, Select, Tag, useFileUploadState, useForm, useSelect } from "@pankod/refine-antd";
import { IResourceComponentsProps, useGetIdentity } from "@pankod/refine-core";
import { RenderParagraphWithImage } from "components/common/renderParagraphWithImage";
import { CancelButton } from "components/utils/cancelButton";
import { ILookup, IUser } from "interfaces";
import { ITechJam } from "interfaces/article";
import { DATAPROVIDER_CREATE, DATAPROVIDER_LOOKUP, RESOURCE_PATH } from "scripts/site";

const DEFAULT_TAG = "Tech Jam";

export const TechJamCreate: React.FC<IResourceComponentsProps> = () => {
  const { data } = useGetIdentity();
  const user = data as IUser;

  const { formProps, saveButtonProps, form } = useForm<ITechJam>({
    dataProviderName: DATAPROVIDER_CREATE,
    redirect: "show",
  });

  const handleFinish = (values: any) => {
    // Ensure Tech Jam tag is always included
    const tags = values.tagList || [];
    if (!tags.includes(DEFAULT_TAG)) {
      tags.unshift(DEFAULT_TAG);
    }
    // Call the original onFinish from formProps
    return formProps.onFinish?.({ ...values, tagList: tags });
  };

  const { isLoading: isUploading, onChange: onUploadChange } = useFileUploadState();

  const { selectProps: usersList } = useSelect<ILookup>({
    dataProviderName: DATAPROVIDER_LOOKUP,
    resource: RESOURCE_PATH.PEOPLE,
    optionLabel: "nameEmail",
    optionValue: "id",
    sort: [{
      field: "lastAccessedAt",
      order: "desc"
    }],
    filters: [{
      field: "stateManager.state",
      operator: "eq",
      value: "active"
    }],
  });

  return (
    <Create
      title="Create a Session"
      footerButtons={() => (
        <>
          <SaveButton {...saveButtonProps} disabled={isUploading} />
          <CancelButton />
        </>
      )}
    >
      <Form {...formProps} layout="vertical" autoComplete="off"
        onFinish={handleFinish}
        initialValues={{
          body: [{ paragraph: "" }],
          authorName: user?.id ? [user?.id] : [],
          tagList: ["Tech Jam"]
        }}
      >
        <Form.Item
          label="Title"
          name="name"
          rules={[
            {
              required: true,
              whitespace: true,
              min: 5,
              max: 100
            },
          ]}
        >
          <Input placeholder="Session title" />
        </Form.Item>
        <Form.Item
          label="Short description"
          name="description"
          rules={[
            {
              required: true,
              whitespace: true,
              min: 15,
              max: 200
            },
          ]}
        >
          <Input.TextArea rows={2} placeholder="Brief summary covering audience, learning goals, and highlights" />
        </Form.Item>

        <Form.Item
          label="Tags"
          name="tagList"
          tooltip="Add tags. 'Tech Jam' tag is mandatory and cannot be removed."
          rules={[{
            required: true,
            type: "array"
          }]}
        >
          <Select mode="tags"
            placeholder="Type a keyword and press enter. You can add as many as you want."
            tokenSeparators={[',']}
            onChange={(value: string[]) => {
              // Remove duplicate "Tech Jam" tags and ensure only one exists at the start
              const filtered = value.filter(tag => tag !== DEFAULT_TAG);
              const updatedTags = [DEFAULT_TAG, ...filtered];
              form?.setFieldsValue({ tagList: updatedTags });
            }}
            tagRender={(props) => {
              const { label, closable, onClose } = props;
              const isDefaultTag = (label === "Tech Jam");
              return (
                <Tag
                  color={isDefaultTag ? "blue" : "default"}
                  closable={!isDefaultTag && closable}
                  onClose={onClose}
                >{label}</Tag>
              );
            }}
          >
          </Select>
        </Form.Item>
        <Form.Item
          label="Speaker(s)"
          name="authorName"
          rules={[{
            required: true,
            whitespace: true,
            type: "array",
            min: 1,
            max: 3,
            message: "Minimum 1 and maximum of 3 speakers"
          }]}
        >
          <Select placeholder="Search Name or Email" mode="multiple" {...usersList} allowClear />
        </Form.Item>

        <Divider orientation="left">Session Content</Divider>
        <Form.Item
          label="Artifact URL"
          name="artifactUrl"
          tooltip="OneDrive shared folder link containing the session presentation and recording"
          rules={[
            {
              required: true,
              message: "Please provide the artifact URL"
            },
            {
              type: "url",
              message: "Please enter a valid URL"
            }
          ]}
          extra={
            <span style={{ color: "#faad14" }}>
              ⚠️ <strong>Important:</strong> Do <strong>not</strong> share the folder with everyone in the organization.
              Users should request access, which will then be approved by the session host.
            </span>
          }
        >
          <Input placeholder="https://microsoft-my.sharepoint.com/..." />
        </Form.Item>
        <RenderParagraphWithImage
          resourcePath={RESOURCE_PATH.TECHJAM} onUploadChange={onUploadChange}
          paragraphFieldName={"body"} imagesFieldName={"articleImages"} featuredImage={true} />
      </Form>
    </Create>
  );
};